import React, {useState, useEffect} from 'react';
import * as Tone from 'tone';

//The object passed to createContext is the default context that gets used
//when the context is not set by the context provider. 
const ToneContext = React.createContext({
    setSampler: null,
    tenorsSampler: null,
    pianoSampler: null,
    loadPiano: async () => null,
});

// Salamander Grand Piano (CC BY 3.0, see public/samples/piano/ATTRIBUTION.txt): one sample
// every minor third from A0 to C8; Tone.Sampler fills in the notes between.
const PIANO_SAMPLES = Object.fromEntries(["A0", "C1", "D#1", "F#1", "A1", "C2", "D#2", "F#2", "A2", "C3", "D#3", "F#3", "A3", "C4", "D#4", "F#4",
  "A4", "C5", "D#5", "F#5", "A5", "C6", "D#6", "F#6", "A6", "C7", "D#7", "F#7", "A7", "C8"].map((note) => [note, `${note.replace("#", "s")}.mp3`]));
let pianoLoading = null;
// The piano is about 2 MB, so it loads the first time a score or preview needs it.
function loadPianoSampler() {
  if (!pianoLoading) pianoLoading = new Promise((resolve, reject) => {
    const sampler = new Tone.Sampler({ urls: PIANO_SAMPLES, release: 1.2, baseUrl: `${window.location.origin}/samples/piano/`,
      onload: () => resolve(sampler), onerror: (error) => { pianoLoading = null; reject(error); } }).toDestination();
  });
  return pianoLoading;
}

export const ToneContextProvider = props => {
    const [setSampler, setSetSampler] = useState();
    const [cymbalsSampler, setCymbalsSampler] = useState();
    const [bassSampler, setBassSampler] = useState();
    const [tenorsSampler, setTenorsSampler] = useState();
    const [snareSampler, setSnareSampler] = useState();
    const [pianoSampler, setPianoSampler] = useState(null);
    const loadPiano = React.useCallback(() => loadPianoSampler().then((sampler) => { setPianoSampler(sampler); return sampler; }), []);

    //This setup is done in useEffect because it cannot be done server side when the page is being built.
    //This is because Tone.js uses calls that are only available in the browser.
    useEffect(() => {
      const origin = window.location.origin;
        const setSampler = new Tone.Sampler({
            urls: {
              C5: "snare.mp3",
              E5: "hh.mp3",
              D4: 'hhf.mp3',
              F5: 'ride.mp3',
              F4: "kick.mp3",
              D5: "tom1.mp3",
              B4: "tom2.mp3",
              A4: "tom3.mp3",
              G4: "tom4.mp3"
            },
            release: 1,
            baseUrl: `${origin}/samples/drumset/`,
          }).toDestination();
          setSetSampler(setSampler);
        
          const tenorsSampler = new Tone.Sampler({
            urls: {
              G5: 'S.mp3',
              E5: "1.mp3",
              C5: "2.mp3",
              A4: "3.mp3",
              F4: '4.mp3'
            },
            release: 1,
            baseUrl: `${origin}/samples/tenors/`,
          }).toDestination();
          setTenorsSampler(tenorsSampler);

          const snareSampler = new Tone.Sampler({
            urls: {
              C5: 'snare.mp3',
              D5: 'click.mp3',
              E5: 'ping.mp3',
              F5: 'rim.mp3',
              G5: 'buzz.wav'
            },
            release: 1,
            baseUrl: `${origin}/samples/snare/`,
          }).toDestination();
          setSnareSampler(snareSampler);

          const bassSampler = new Tone.Sampler({
            urls: {
              G5: 'b1.mp3',
              E5: 'b2.mp3',
              C5: 'b3.mp3',
              A4: 'b4.mp3',
              F4: 'b5.mp3'
            },
            release: 1,
            baseUrl: `${origin}/samples/bass/`,
          }).toDestination();
          setBassSampler(bassSampler);

          const cymbalsSampler = new Tone.Sampler({
            urls: {
              E5: 'crash.mp3',
              C5: 'choke.mp3',
            },
            release: 1,
            baseUrl: `${origin}/samples/cymbals/`,
          }).toDestination();
          setCymbalsSampler(cymbalsSampler);

          new Tone.PolySynth().toDestination();
      }, []);

    return <ToneContext.Provider value={{
        setSampler,
        snareSampler,
        tenorsSampler,
        bassSampler,
        cymbalsSampler,
        pianoSampler,
        loadPiano
    }}>{props.children}</ToneContext.Provider>
};

export default ToneContext;
